import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail, authFileUrl } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, ShoppingCart, Check, RotateCcw } from "lucide-react";

export default function Shortages() {
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [ro, setRo] = useState(null);       // row being reordered
  const [form, setForm] = useState(null);

  const load = () => api.get("/shortages").then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => {
    load();
    api.get("/ledgers?type=vendor").then((r) => setVendors(r.data || [])).catch(() => {});
  }, []);

  const open = rows.filter((r) => !r.resolved);
  const done = rows.filter((r) => r.resolved);

  const openReorder = (r) => {
    setRo(r);
    setForm({ item: r.shortage_note || r.stage_name || "", qty: "1", unit: "nos", vendor_id: "", rate: "" });
  };
  const submitReorder = async () => {
    try {
      const { data } = await api.post(`/shortages/${ro.id}/reorder`, {
        item: form.item, qty: Number(form.qty) || 1, unit: form.unit,
        vendor_id: form.vendor_id, rate: Number(form.rate) || 0,
      });
      toast.success(`Draft PO ${data?.po_number || ""} created — see Purchase Orders`);
      setRo(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const resolve = async (r, val) => {
    try { await api.post(`/shortages/${r.id}/resolve`, { resolved: val }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const Row = ({ r }) => (
    <div data-testid={`shortage-${r.id}`} className={`flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2.5 ${r.resolved ? "border-border bg-muted/30 opacity-80" : "border-ochre/40 bg-ochre/5"}`}>
      <div className="flex items-center gap-3">
        {(r.photos || [])[0] && <img src={authFileUrl(r.photos[0])} alt="" className="h-11 w-11 rounded border border-border object-cover" />}
        <div>
          <p className="text-sm font-semibold">{r.shortage_note || "(no detail)"}</p>
          <p className="text-xs text-muted-foreground">{r.product_name} · {r.stage_name} · {r.date} {r.slot} · {r.created_by_name}</p>
          {r.reorder_po_number && <p className="text-xs text-primary">Draft {r.reorder_po_number}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {!r.resolved ? (
          <>
            <Button size="sm" data-testid={`shortage-reorder-${r.id}`} onClick={() => openReorder(r)} className="h-8 gap-1.5"><ShoppingCart className="h-3.5 w-3.5" /> Draft PO</Button>
            <Button size="sm" variant="outline" data-testid={`shortage-resolve-${r.id}`} onClick={() => resolve(r, true)} className="h-8 gap-1.5"><Check className="h-3.5 w-3.5" /> Resolve</Button>
          </>
        ) : (
          <Button size="sm" variant="ghost" data-testid={`shortage-reopen-${r.id}`} onClick={() => resolve(r, false)} className="h-8 gap-1.5 text-muted-foreground"><RotateCcw className="h-3.5 w-3.5" /> Reopen</Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      <PageHeader title="Shortage Inbox" subtitle="Every material-shortage flagged by supervisors — reorder in one tap." icon={AlertTriangle} />

      {rows.length === 0 ? (
        <EmptyState icon={AlertTriangle} title="No shortages flagged" hint="When a supervisor marks a material shortage in a daily update, it lands here." />
      ) : (
        <div className="space-y-6">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-ochre">Open ({open.length})</p>
            {open.length === 0 ? <p className="text-sm text-muted-foreground">Nothing pending — all clear.</p> : (
              <div className="space-y-2" data-testid="shortage-open-list">{open.map((r) => <Row key={r.id} r={r} />)}</div>
            )}
          </div>
          {done.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Resolved ({done.length})</p>
              <div className="space-y-2" data-testid="shortage-done-list">{done.map((r) => <Row key={r.id} r={r} />)}</div>
            </div>
          )}
        </div>
      )}

      <Dialog open={!!ro} onOpenChange={(v) => !v && setRo(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Reorder draft — {ro?.product_name}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">Creates a <b>draft Purchase Order</b> and marks this shortage resolved. Adjust vendor/price later in Purchase Orders.</p>
              <div className="space-y-2"><Label>Item</Label>
                <Input data-testid="reorder-item" value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} placeholder="e.g. 4-inch hinges" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2"><Label>Qty</Label><Input data-testid="reorder-qty" type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} /></div>
                <div className="space-y-2"><Label>Unit</Label><Input data-testid="reorder-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
                <div className="space-y-2"><Label>Rate ₹</Label><Input data-testid="reorder-rate" type="number" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} placeholder="auto" /></div>
              </div>
              <div className="space-y-2"><Label>Vendor (optional — remembers last if blank)</Label>
                <Select value={form.vendor_id || "none"} onValueChange={(v) => setForm({ ...form, vendor_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="reorder-vendor"><SelectValue placeholder="Usual / pick vendor" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Usual supplier (auto)</SelectItem>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
          )}
          <DialogFooter><Button data-testid="reorder-submit" onClick={submitReorder} disabled={!form?.item}>Create Draft PO</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
